import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Inscription, PriceInput, useDebounce } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { LockboxValidationError, lockboxValidationErrorTitle } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

import useModifyClaimRepayments from '../hooks/useModifyClaimRepayments';

const DEFAULT_AMOUNT = '';

interface Props {
  claimId?: string;
  isSelected?: boolean;
  disabled?: boolean;
  isEditMode?: boolean;
}

const AllocateClaimAmount: React.FC<Props> = ({ claimId, isSelected, disabled, isEditMode }) => {
  const { editableLockbox, originalLockbox, validationErrors, setEditableLockbox } = useLockboxDetailsStore();

  const defaultAllocateAmount = useMemo(() => (
    editableLockbox?.claimRepayments?.find((claim) => claim?.claimId === claimId)?.repaymentAmount
    || originalLockbox?.createdClaimRepayments?.find((claim) => claim?.claimId === claimId)?.repaymentAmount
    || DEFAULT_AMOUNT
  ), [editableLockbox?.claimRepayments, originalLockbox?.createdClaimRepayments, claimId]);

  const [allocateAmount, setAllocateAmount] = useState(defaultAllocateAmount);
  const debounceAllocateAmount = useDebounce(allocateAmount);
  const { setClaimAllocateAmount } = useModifyClaimRepayments();
  const allocateAmountFromState = editableLockbox?.claimRepayments?.find((claim) => claim?.claimId === claimId)?.repaymentAmount;

  const totalAllocated = useMemo(() => {
    const editableTotal = editableLockbox?.claimRepayments?.reduce((sum, item) => sum + toNumber(item.repaymentAmount || 0), 0) || 0;
    const createdTotal = originalLockbox?.createdClaimRepayments?.reduce((sum, item) => sum + toNumber(item.repaymentAmount || 0), 0) || 0;
    return Math.round((editableTotal + createdTotal) * 100) / 100;
  }, [editableLockbox?.claimRepayments, originalLockbox?.createdClaimRepayments]);

  const allocateAmountNumber = useMemo(() => toNumber(allocateAmount), [allocateAmount]);

  const error = useMemo(() => {
    if (validationErrors?.find((error) => error === LockboxValidationError.NEED_TO_ALLOCATE_CLAIM) && !allocateAmountNumber) {
      return LockboxValidationError.NEED_TO_ALLOCATE_CLAIM;
    }

    const claimAmount = editableLockbox?.claimRepayments?.find((claimRepayment) => claimRepayment.claimId === claimId)?.claimAmount || 0;

    if (validationErrors?.find((error) => error === LockboxValidationError.TOO_MUCH_ALLOCATED) && allocateAmountNumber > claimAmount) {
      return LockboxValidationError.TOO_MUCH_ALLOCATED;
    }
  }, [validationErrors, allocateAmountNumber, editableLockbox?.claimRepayments, claimId]);

  const handleSetAllocateAmount = useCallback((value: string) => {
    if (!value) {
      setAllocateAmount(DEFAULT_AMOUNT);
      return;
    }

    setAllocateAmount(value);
  }, []);

  useEffect(() => {
    setClaimAllocateAmount(claimId, debounceAllocateAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceAllocateAmount, claimId]);

  useEffect(() => {
    if (!isSelected && allocateAmount && isEditMode) {
      setAllocateAmount(DEFAULT_AMOUNT);
    }
  }, [isSelected, allocateAmount, isEditMode]);

  // sync amount from store
  useEffect(() => {
    if (disabled && allocateAmountFromState && allocateAmountFromState !== allocateAmount) {
      setAllocateAmount(allocateAmountFromState);
    }
  }, [allocateAmountFromState, allocateAmount, disabled]);

  useEffect(() => {
    setEditableLockbox({ totalAllocated });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAllocated]);  

  return (
    <Box direction="row" justify="between" align="center">
      <Inscription color="textSecondary">Allocate:</Inscription>

      <Box width={{ width: '100%', max: '140px' }}>
        <PriceInput
          testId="allocate-amount"
          name="allocate-amount"
          value={allocateAmount}
          onChange={handleSetAllocateAmount}
          disabled={!isSelected || disabled}
          placeholder="$0.00"
          inputStyle={{ textAlign: 'center' }}
          isBold
          errorText={error && !disabled && isSelected
            ? lockboxValidationErrorTitle[error]
            : undefined}
        />
      </Box>
    </Box>
  );
};

export default AllocateClaimAmount;
